import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  thumbnailGenerationApi,
  IThumbnailGenerationJob,
  IGeneratedThumbnail,
} from '@/services/thumbnailGenerationApi';
import Pagination, { paginate } from '@/components/Common/Pagination';

interface ThumbnailWithUrl extends IGeneratedThumbnail {
  signedUrl?: string;
}

const ITEMS_PER_PAGE = 10;

export default function ThumbnailGeneratorPage() {
  const [videoS3Url, setVideoS3Url] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [transcript, setTranscript] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#FF0000');
  const [secondaryColor, setSecondaryColor] = useState('#FFFFFF');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState<IThumbnailGenerationJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<IThumbnailGenerationJob | null>(null);
  const [thumbnails, setThumbnails] = useState<ThumbnailWithUrl[]>([]);
  const [advancedMode, setAdvancedMode] = useState(false);
  const [editTexts, setEditTexts] = useState<Record<string, string>>({});
  const [regenerating, setRegenerating] = useState<Record<string, boolean>>({});
  const [jobsPage, setJobsPage] = useState(1);

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    if (selectedJob.status !== 'queued' && selectedJob.status !== 'processing') {
      loadSignedUrls(selectedJob);
      return;
    }
    const interval = setInterval(() => {
      refreshJob(selectedJob.jobId);
    }, 3000);
    return () => clearInterval(interval);
  }, [selectedJob]);

  useEffect(() => {
    if (jobs.some((j) => j.status === 'queued' || j.status === 'processing')) {
      const interval = setInterval(loadJobs, 5000);
      return () => clearInterval(interval);
    }
  }, [jobs]);

  const loadJobs = async () => {
    try {
      const res = await thumbnailGenerationApi.getJobs();
      if (res.data.success) {
        setJobs(res.data.jobs as IThumbnailGenerationJob[]);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load thumbnail jobs');
    }
  };

  const refreshJob = async (jobId: string) => {
    try {
      const res = await thumbnailGenerationApi.getJob(jobId);
      if (res.data.success) {
        const job = res.data.job as IThumbnailGenerationJob;
        setSelectedJob(job);
        setJobs((prev) => prev.map((j) => (j.jobId === job.jobId ? job : j)));
        if (job.status === 'completed') {
          loadSignedUrls(job);
        }
      }
    } catch (err: any) {
      // ignore polling errors
    }
  };

  const loadSignedUrls = async (job: IThumbnailGenerationJob) => {
    const thumbs: ThumbnailWithUrl[] = job.topThumbnails || [];
    const withUrls = await Promise.all(
      thumbs.map(async (thumb) => {
        if (!thumb.s3Key) return thumb;
        try {
          const res = await thumbnailGenerationApi.getThumbnailSignedUrl(job.jobId, thumb.thumbnailId);
          return { ...thumb, signedUrl: res.data.url };
        } catch {
          return thumb;
        }
      })
    );
    setThumbnails(withUrls);
    setEditTexts(Object.fromEntries(withUrls.map((t) => [t.thumbnailId, t.textOverlay])));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoS3Url) {
      toast.error('Video URL is required');
      return;
    }
    setLoading(true);
    try {
      const res = await thumbnailGenerationApi.createJob({
        videoS3Url,
        title,
        description,
        transcript,
        brandKit: { primaryColor, secondaryColor },
      });
      if (res.data.success) {
        toast.success('Thumbnail generation started');
        await refreshJob(res.data.jobId);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start thumbnail generation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateText = async (jobId: string, thumbnailId: string) => {
    const text = editTexts[thumbnailId];
    if (!text) return;
    setRegenerating((prev) => ({ ...prev, [thumbnailId]: true }));
    try {
      await thumbnailGenerationApi.updateThumbnailText(jobId, thumbnailId, text);
      toast.success('Thumbnail regenerated with new hook text');
      await refreshJob(jobId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to regenerate thumbnail');
    } finally {
      setRegenerating((prev) => ({ ...prev, [thumbnailId]: false }));
    }
  };

  const paginatedJobs = paginate(jobs, jobsPage, ITEMS_PER_PAGE);
  const jobsTotalPages = Math.ceil(jobs.length / ITEMS_PER_PAGE);

  const handleDelete = async (jobId: string) => {
    try {
      await thumbnailGenerationApi.deleteJob(jobId);
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
      if (selectedJob?.jobId === jobId) {
        setSelectedJob(null);
        setThumbnails([]);
      }
      toast.success('Job deleted');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete job');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AI Thumbnail Generator</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Generate top 10 click-optimized thumbnails from a video in under 60 seconds.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Video S3 URL</label>
          <input
            type="url"
            value={videoS3Url}
            onChange={(e) => setVideoS3Url(e.target.value)}
            placeholder="https://... or s3 key"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Video title"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Transcript / Hook text</label>
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={3}
            placeholder="Paste transcript to extract hook text"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary text color</label>
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="mt-1 block w-full h-10 rounded-md border-gray-300 dark:border-gray-600"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Secondary text color</label>
            <input
              type="color"
              value={secondaryColor}
              onChange={(e) => setSecondaryColor(e.target.value)}
              className="mt-1 block w-full h-10 rounded-md border-gray-300 dark:border-gray-600"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {loading ? 'Starting...' : 'Generate Top 10 Thumbnails'}
        </button>
      </form>

      {selectedJob && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Job: {selectedJob.jobId}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {selectedJob.status} · {selectedJob.progress}% · {selectedJob.currentStep}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={advancedMode}
                  onChange={(e) => setAdvancedMode(e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Advanced edit
              </label>
              <button
                type="button"
                onClick={() => handleDelete(selectedJob.jobId)}
                className="text-red-600 dark:text-red-400 hover:underline text-sm"
              >
                Delete
              </button>
            </div>
          </div>

          {selectedJob.status === 'completed' && thumbnails.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {thumbnails.map((thumb) => (
                <div
                  key={thumb.thumbnailId}
                  className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                >
                  {thumb.signedUrl ? (
                    <img
                      src={thumb.signedUrl}
                      alt={`Rank ${thumb.rank} thumbnail`}
                      className="w-full aspect-video object-cover"
                    />
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center bg-gray-200 dark:bg-gray-700 text-sm text-gray-500">
                      No preview
                    </div>
                  )}
                  <div className="p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">#{thumb.rank}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        CTR {Math.round(thumb.ctrScore * 100)}%
                      </span>
                    </div>
                    {advancedMode ? (
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={editTexts[thumb.thumbnailId] ?? thumb.textOverlay}
                          onChange={(e) =>
                            setEditTexts((prev) => ({ ...prev, [thumb.thumbnailId]: e.target.value }))
                          }
                          disabled={regenerating[thumb.thumbnailId]}
                          className="w-full text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-900 dark:text-white"
                        />
                        <button
                          type="button"
                          onClick={() => selectedJob && handleUpdateText(selectedJob.jobId, thumb.thumbnailId)}
                          disabled={regenerating[thumb.thumbnailId]}
                          className="w-full text-[10px] rounded bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-200 px-2 py-1 disabled:opacity-50"
                        >
                          {regenerating[thumb.thumbnailId] ? 'Regenerating...' : 'Regenerate'}
                        </button>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2" title={thumb.textOverlay}>
                        {thumb.textOverlay}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">{thumb.layout}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedJob.status === 'failed' && (
            <p className="text-sm text-red-600 dark:text-red-400">{selectedJob.error || 'Generation failed'}</p>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Jobs</h3>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {paginatedJobs.map((job) => (
            <li key={job.jobId} className="py-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setSelectedJob(job);
                  if (job.status === 'completed') loadSignedUrls(job);
                }}
                className="text-left flex-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md p-2 -ml-2 transition-colors"
              >
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-md">
                  {job.title || job.jobId}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {job.status} · {job.progress}% · {job.currentStep}
                </p>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(job.jobId)}
                className="text-red-600 dark:text-red-400 hover:underline text-sm ml-4"
              >
                Delete
              </button>
            </li>
          ))}
          {jobs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No jobs yet.</p>}
        </ul>
        <Pagination
          currentPage={jobsPage}
          totalPages={jobsTotalPages}
          onPageChange={setJobsPage}
          totalItems={jobs.length}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      </div>
    </div>
  );
}
